import React, { useEffect, useState } from 'react'
import {
	Button, Drawer, notification, Tag, Typography
} from 'antd'
import moment from 'moment'
// eslint-disable-next-line import/no-cycle
import SelectPriceOfferHeaderOptionField, { ISelectedFirmHeaderOption } from '../../components/SelectPriceOfferHeaderOptionField'
import SelectUsersQueryField from '../../components/SelectUsersQueryField'
// eslint-disable-next-line import/no-cycle
import StoreItems from '../storeItems/StoreItems'
import { StoreItemTableItem } from '../../reducers/storeItems/actions'
import SignaturePad, { resolveSignatureSubmit, SignatureSubmitState } from './SignaturePad'
import { createHandoverProtocol } from './handoverProtocolApi'
import { CreateHandoverProtocolPayload, HandoverProtocolType } from './types'
import { COMMITMENT_TEXT_PLAIN } from './commitmentText'
import { USER_ROLE } from '../../utils/enums'

export interface IBuildCreatePayloadParams {
	type: HandoverProtocolType
	jobID?: number | null
	userID?: number | null
	defaultRecipientUserID: number | null
	selectedRecipientUserID: number | null
	optionHeaderId: number | null
	signatureBlob: Blob | null
}

/**
 * recipientUserID sa posiela LEN ked manazer preberajuceho naozaj prepol - server z toho
 * odvodzuje priznak manuallyReassigned v snapshote (REQ-14-6).
 * userID zostava vlastnikom osobneho skladu - server si z neho odvodi personalStoreUserID,
 * aby protokol nezmizol z profilu vlastnika, ked ho podpisal zaskok.
 */
export const buildCreatePayload = (params: IBuildCreatePayloadParams): CreateHandoverProtocolPayload => {
	if (!params.optionHeaderId) throw new Error('Vyberte hlavičku')
	if (!params.signatureBlob) throw new Error('Chýba podpis')
	const payload: CreateHandoverProtocolPayload = {
		type: params.type,
		jobID: params.type === 'JOB' ? params.jobID ?? null : null,
		userID: params.type === 'PERSONAL' ? params.userID ?? null : null,
		optionHeaderId: params.optionHeaderId,
		signatureBlob: params.signatureBlob
	}
	if (params.selectedRecipientUserID && params.selectedRecipientUserID !== params.defaultRecipientUserID) {
		payload.recipientUserID = params.selectedRecipientUserID
	}
	return payload
}

type Props = {
	visible: boolean
	type: HandoverProtocolType
	jobID?: number | null
	userID?: number | null
	title: string // adresa zakazky alebo meno zamestnanca
	defaultRecipientUserID: number | null
	defaultRecipientName: string | null
	storeItemsProps: { tableData: StoreItemTableItem[] | null | undefined, initFilter: { jobID: number | null, jobToID: number | null | undefined } }
	onCancel: () => void
	onCreated: () => void // rodic po nom refetchne zoznam
}

const HandoverProtocolPreview: React.FC<Props> = ({
	visible, type, jobID, userID, title, defaultRecipientUserID, defaultRecipientName, storeItemsProps, onCancel, onCreated
}) => {
	const [headerValue, setHeaderValue] = useState<ISelectedFirmHeaderOption | undefined | null>(undefined)
	const [selectedRecipientUserID, setSelectedRecipientUserID] = useState<number | null>(defaultRecipientUserID)
	const [isSignatureVisible, setIsSignatureVisible] = useState(false)
	const [submitState, setSubmitState] = useState<SignatureSubmitState>('IDLE')
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [lastBlob, setLastBlob] = useState<Blob | null>(null)

	// Kazde otvorenie naneho zacina cisty stav - predosla rozpisana signatura z ineho protokolu sa
	// nesmie objavit v novom.
	useEffect(() => {
		if (visible) {
			setSelectedRecipientUserID(defaultRecipientUserID)
			setHeaderValue(undefined)
			setIsSignatureVisible(false)
			setSubmitState('IDLE')
			setErrorMessage(null)
			setLastBlob(null)
		}
	}, [visible, defaultRecipientUserID])

	// Stavovy automat z 14-02 je jediny zdroj pravdy o tom, kedy sa platno cisti - podpis sa pri
	// zlyhani uploadu NEZAHADZUJE (REQ-14-14).
	const submit = async (blob: Blob) => {
		setLastBlob(blob)
		setSubmitState(resolveSignatureSubmit(submitState, 'SUBMIT').state)
		setErrorMessage(null)
		try {
			const payload = buildCreatePayload({
				type,
				jobID,
				userID,
				defaultRecipientUserID,
				selectedRecipientUserID,
				optionHeaderId: headerValue?.value ?? null,
				signatureBlob: blob
			})
			await createHandoverProtocol(payload)
			const done = resolveSignatureSubmit('SAVING', 'SUCCESS')
			setSubmitState(done.state)
			notification.success({ message: 'Preberací protokol bol podpísaný a uložený' })
			setIsSignatureVisible(false)
			onCreated()
		} catch (e: any) {
			const failed = resolveSignatureSubmit('SAVING', 'FAILURE')
			setSubmitState(failed.state)
			setErrorMessage(e?.response?.data?.messages?.[0]?.message || e?.message || 'Protokol sa nepodarilo uložiť. Skúste to znova.')
		}
	}

	const handleSignatureSave = (blob: Blob) => submit(blob)
	const handleRetry = () => { if (lastBlob) submit(lastBlob) }

	// Bez hlavicky nema PDF co vygenerovat; zakazka bez priradeneho pracovnika nesmie byt podpisana
	// na niekoho neurcenom - manazer musi preberajuceho vybrat rucne cez prepnutie.
	const canSign = !!headerValue && (type === 'PERSONAL' || !!defaultRecipientUserID || !!selectedRecipientUserID)

	return (
		<Drawer
			visible={visible}
			placement={'right'}
			width={'100%'}
			onClose={onCancel}
			destroyOnClose
			title={'Nový preberací protokol'}
		>
			<div style={{ paddingBottom: 96 }}>
				<Typography.Title level={5}>Hlavička firmy</Typography.Title>
				<SelectPriceOfferHeaderOptionField onValueChange={setHeaderValue} headerValue={headerValue} />

				<Typography.Title level={5} style={{ marginTop: 24 }}>Preberajúci</Typography.Title>
				<div>{defaultRecipientName || '-'}</div>
				{type === 'JOB' && !defaultRecipientUserID && (
					<Typography.Text type={'danger'}>Zákazka nemá priradeného pracovníka</Typography.Text>
				)}
				<div style={{ marginTop: 8 }}>
					<SelectUsersQueryField
						multiple={false}
						/* preberajucim moze byt len zamestnanec - adminov, telefonistky
						   ani zodpovedne osoby do vyberu nepatria */
						queryParams={{ role: [USER_ROLE.USER] }}
						value={[selectedRecipientUserID]}
						onChange={(v) => setSelectedRecipientUserID(v as unknown as number)}
						placeholder={'Vyber zamestnanca'}
					/>
				</div>
				{selectedRecipientUserID !== defaultRecipientUserID && (
					<Tag color={'orange'} style={{ marginTop: 8 }}>Preberajúceho ručne zmenil manažér</Tag>
				)}

				<Typography.Title level={5} style={{ marginTop: 24 }}>Adresa</Typography.Title>
				<div>{title}</div>

				<Typography.Title level={5} style={{ marginTop: 24 }}>Dátum</Typography.Title>
				<div>{moment().format('DD.MM.YYYY')}</div>

				<Typography.Title level={5} style={{ marginTop: 24 }}>Položky</Typography.Title>
				<StoreItems
					tableMode
					tableData={storeItemsProps.tableData}
					initFilter={storeItemsProps.initFilter}
					addRemoveItemFeature={false}
					contextStore={null}
				/>
				<Typography.Text type={'secondary'}>Zoznam a ceny sa pri podpise znovu načítajú zo servera.</Typography.Text>

				<Typography.Title level={5} style={{ marginTop: 24 }}>Záväzok</Typography.Title>
				<div style={{
					border: '1px solid #f0f0f0', borderRadius: 4, padding: 16, background: '#fafafa'
				}}
				>
					<Typography.Paragraph>{COMMITMENT_TEXT_PLAIN}</Typography.Paragraph>
				</div>
			</div>

			<div style={{
				position: 'sticky', bottom: 0, background: '#fff', padding: 16, borderTop: '1px solid #f0f0f0'
			}}
			>
				<Button
					type={'primary'}
					size={'large'}
					block
					disabled={!canSign}
					onClick={() => setIsSignatureVisible(true)}
				>
					Podpísať
				</Button>
			</div>

			<SignaturePad
				visible={isSignatureVisible}
				saving={submitState === 'SAVING'}
				errorMessage={errorMessage}
				onCancel={() => setIsSignatureVisible(false)}
				onSave={handleSignatureSave}
				onRetry={handleRetry}
			/>
		</Drawer>
	)
}

export default HandoverProtocolPreview
